import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faCheck,
  faX
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const RepairRulesManagement = () => {
  const [repairRules, setRepairRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    corrosionDamageCombinationId: null,
    repairProcessTypeId: null,
    isRequired: true,
    sequenceOrder: 1,
    stripingProportion: null,
    notes: '',
  });
  
  // Options for dropdowns
  const [corrosionDamageCombinations, setCorrosionDamageCombinations] = useState([]);
  const [repairProcessTypes, setRepairProcessTypes] = useState([]);
  
  // Matrix data structure
  const [matrixData, setMatrixData] = useState({});

  // Fixed order mapping: Wash=1, Prime=2, Stripe=3, Midcoat=4, Topcoat=5
  const getProcessOrder = (processName) => {
    const orderMap = {
      'Wash': 1,
      'Prime': 2,
      'Stripe': 3,
      'Midcoat': 4,
      'Topcoat': 5
    };
    return orderMap[processName] || 999;
  };

  // Sort process types in fixed order
  const sortProcessTypes = (processTypes) => {
    return [...processTypes].sort((a, b) => {
      const orderA = getProcessOrder(a.processName);
      const orderB = getProcessOrder(b.processName);
      return orderA - orderB;
    });
  };

  // Fetch options for dropdowns
  const fetchOptions = async () => {
    try {
      const [combinationsResponse, processTypesResponse] = await Promise.all([
        api.get('/corrosion-damage-combinations', { params: { limit: 500 } }),
        api.get('/repair-process-types')
      ]);

      if (combinationsResponse.data.success) {
        setCorrosionDamageCombinations(combinationsResponse.data.data);
      }
      if (processTypesResponse.data.success) {
        // Touchup and Touchup Overcoat cannot be steps in a full repair - exclude from Repair Rules matrix
        const fullRepairOnly = (processTypesResponse.data.data || []).filter(
          (p) => p.processName !== 'Touchup' && p.processName !== 'Touchup Overcoat'
        );
        const sorted = sortProcessTypes(fullRepairOnly);
        setRepairProcessTypes(sorted);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  // Build matrix data structure
  const buildMatrixData = (rules, combinations, processTypes) => {
    const matrix = {};
    
    // Initialize matrix with all combinations
    combinations.forEach(combination => {
      matrix[combination.id] = {
        combination: combination,
        processes: {}
      };
      
      // Initialize all process types as not required
      processTypes.forEach(processType => {
        matrix[combination.id].processes[processType.id] = {
          processType: processType,
          isRequired: false,
          sequenceOrder: null,
          stripingProportion: null,
          notes: '',
          ruleId: null
        };
      });
    });
    
    // Populate matrix with existing rules
    rules.forEach(rule => {
      const combinationId = rule.corrosionDamageCombinationId;
      const processTypeId = rule.repairProcessTypeId;
      
      if (matrix[combinationId] && matrix[combinationId].processes[processTypeId]) {
        matrix[combinationId].processes[processTypeId] = {
          processType: processTypes.find(p => p.id === processTypeId),
          isRequired: rule.isRequired,
          sequenceOrder: rule.sequenceOrder,
          stripingProportion: rule.stripingProportion,
          notes: rule.notes || '',
          ruleId: rule.id
        };
      }
    });
    
    return matrix;
  };

  // Fetch repair rules
  const fetchRepairRules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/repair-rules-new?limit=500');
      
      if (response.data.success) {
        setRepairRules(response.data.data);
        
        // Build matrix data if we have both combinations and process types
        if (corrosionDamageCombinations.length > 0 && repairProcessTypes.length > 0) {
          const matrix = buildMatrixData(response.data.data, corrosionDamageCombinations, repairProcessTypes);
          setMatrixData(matrix);
        }
      }
    } catch (error) {
      console.error('Error fetching repair rules:', error);
      setError('Failed to fetch repair rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchRepairRules();
  }, []);

  // Rebuild matrix when combinations, process types, or rules change
  useEffect(() => {
    if (corrosionDamageCombinations.length > 0 && repairProcessTypes.length > 0 && repairRules.length > 0) {
      const matrix = buildMatrixData(repairRules, corrosionDamageCombinations, repairProcessTypes);
      setMatrixData(matrix);
    }
  }, [corrosionDamageCombinations, repairProcessTypes, repairRules]);

  const resetForm = () => {
    setFormData({
      corrosionDamageCombinationId: '',
      repairProcessTypeId: '',
      isRequired: true,
      sequenceOrder: 1,
      stripingProportion: null,
      notes: '',
    });
    setEditingRule(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRule) {
        await api.put(`/repair-rules-new/${editingRule.id}`, formData);
      } else {
        await api.post('/repair-rules-new', formData);
      }
      
      setShowModal(false);
      resetForm();
      fetchRepairRules();
    } catch (error) {
      console.error('Error saving repair rule:', error);
      setError('Failed to save repair rule');
    }
  };

  const handleEdit = (rule) => {
    setFormData({
      corrosionDamageCombinationId: rule.corrosionDamageCombinationId,
      repairProcessTypeId: rule.repairProcessTypeId,
      isRequired: rule.isRequired,
      sequenceOrder: rule.sequenceOrder,
      stripingProportion: rule.stripingProportion,
      notes: rule.notes || '',
    });
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this repair rule?')) {
      try {
        await api.delete(`/repair-rules-new/${id}`);
        fetchRepairRules();
      } catch (error) {
        console.error('Error deleting repair rule:', error);
        setError('Failed to delete repair rule');
      }
    }
  };

  // Handle matrix cell click
  const handleMatrixCellClick = (combinationId, processTypeId) => {
    const cellData = matrixData[combinationId]?.processes[processTypeId];
    const processType = repairProcessTypes.find(p => p.id === processTypeId);
    
    // Auto-calculate sequence order based on process type
    const autoSequenceOrder = getProcessOrder(processType?.processName || '');
    
    if (cellData) {
      setFormData({
        corrosionDamageCombinationId: combinationId,
        repairProcessTypeId: processTypeId,
        isRequired: cellData.isRequired,
        sequenceOrder: autoSequenceOrder, // Use fixed order
        stripingProportion: cellData.stripingProportion,
        notes: cellData.notes,
      });
      setEditingRule(cellData.ruleId ? { id: cellData.ruleId } : null);
    } else {
      setFormData({
        corrosionDamageCombinationId: combinationId,
        repairProcessTypeId: processTypeId,
        isRequired: true,
        sequenceOrder: autoSequenceOrder, // Use fixed order
        stripingProportion: null,
        notes: '',
      });
      setEditingRule(null);
    }
    setShowModal(true);
  };



  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Repair Rules</h1>
        <p className="text-gray-600 mt-2">Manage repair process rules for corrosion damage combinations</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {(() => {
        const combinationIdsWithRules = new Set((repairRules || []).map(r => r.corrosionDamageCombinationId));
        const combinationsWithNoRules = (corrosionDamageCombinations || []).filter(
          c => !combinationIdsWithRules.has(c.id)
        );
        if (combinationsWithNoRules.length === 0) return null;
        const codes = combinationsWithNoRules.map(c => c.combinationCode).filter(Boolean);
        return (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-amber-800 text-sm font-medium">The following combinations have no Repair Rules configured:</p>
            <p className="text-amber-700 text-sm mt-1">{codes.join(', ')}</p>
            <p className="text-amber-600 text-xs mt-2">Click a cell in the matrix below to add rules for each combination so simulation and repair projects use the correct process steps.</p>
          </div>
        );
      })()}

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-700">Matrix View - Click any cell to add/edit repair rules</h2>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Rule
        </button>
      </div>

      {/* Matrix View */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-100">
                    Combination
                  </th>
                  {repairProcessTypes.map((processType) => (
                    <th key={processType.id} className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-100">
                      {processType.processName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.values(matrixData).map((rowData) => (
                  <tr key={rowData.combination.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-50">
                      <div className="font-semibold text-base">{rowData.combination.combinationCode}</div>
                    </td>
                    {repairProcessTypes.map((processType) => {
                      const cellData = rowData.processes[processType.id];
                      return (
                        <td key={processType.id} className="px-2 py-2 text-center border-r border-gray-200">
                          <div className="flex flex-col items-center space-y-1">
                            <button
                              onClick={() => handleMatrixCellClick(rowData.combination.id, processType.id)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                cellData?.isRequired
                                  ? 'bg-green-500 hover:bg-green-600'
                                  : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                              title={`Click to ${cellData?.isRequired ? 'edit' : 'add'} ${processType.processName} for ${rowData.combination.combinationCode}`}
                            >
                              {cellData?.isRequired ? (
                                <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                              ) : (
                                <FontAwesomeIcon icon={faX} className="text-gray-600 text-xs" />
                              )}
                            </button>
                            {cellData?.stripingProportion && (
                              <div className="text-xs text-blue-600 font-semibold">
                                {cellData.stripingProportion}%
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Matrix Legend */}
          <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-xs text-gray-700">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                </div>
                <span className="font-medium">Required Process</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faX} className="text-gray-600 text-xs" />
                </div>
                <span className="font-medium">Not Required</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-semibold">15%</span>
                <span className="font-medium">Striping Proportion</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Fixed Order: Wash → Prime → Stripe → Midcoat → Topcoat</span>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingRule ? 'Edit Repair Rule' : 'Add New Repair Rule'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Corrosion Damage Combination *
                    </label>
                    <select
                      required
                      value={formData.corrosionDamageCombinationId}
                      onChange={(e) => setFormData({ ...formData, corrosionDamageCombinationId: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Combination</option>
                      {corrosionDamageCombinations.map((combination) => (
                        <option key={combination.id} value={combination.id}>
                          {combination.combinationCode} - {combination.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repair Process Type *
                    </label>
                    <select
                      required
                      value={formData.repairProcessTypeId}
                      onChange={(e) => {
                        const selectedProcessType = repairProcessTypes.find(p => p.id === parseInt(e.target.value));
                        const autoOrder = getProcessOrder(selectedProcessType?.processName || '');
                        setFormData({ 
                          ...formData, 
                          repairProcessTypeId: parseInt(e.target.value),
                          sequenceOrder: autoOrder // Auto-update sequence order
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Process Type</option>
                      {repairProcessTypes.map((processType) => (
                        <option key={processType.id} value={processType.id}>
                          {processType.processName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sequence Order (Fixed)
                    </label>
                    <input
                      type="number"
                      value={formData.sequenceOrder}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                      title="Sequence order is automatically set based on process type (Wash=1, Prime=2, Stripe=3, Midcoat=4, Topcoat=5)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Automatically set: Wash=1, Prime=2, Stripe=3, Midcoat=4, Topcoat=5
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Striping Proportion (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.stripingProportion || ''}
                      onChange={(e) => setFormData({ ...formData, stripingProportion: e.target.value ? parseFloat(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 15.00"
                    />
                  </div>
                  <div className="mb-4 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Process
                    </label>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isRequired}
                        onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        This process is required for this combination
                      </label>
                    </div>
                  </div>
                  <div className="mb-4 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder="Additional notes about this repair rule"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingRule ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairRulesManagement;
